import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { FaTelegramPlane } from "react-icons/fa";
import useApi from "../hooks/useApi";

// Avisos por Telegram (Backend/src/utils/telegram). La cuenta se conecta con
// un enlace de un solo uso que se genera aquí, con la sesión iniciada: el bot
// no sabe quién es quién, lo sabe Radar Freim. Mientras el enlace está
// abierto, la sección pregunta cada pocos segundos si ya se conectó, pero solo
// unos minutos: cada pregunta cuenta para el límite de pedidos por IP de la
// API (300 cada 15 min), y pasarlo deja a la persona sin poder usar nada.
// Después, revisa al volver a esta pestaña o con "Ya toqué «Iniciar»".

const CADA_MS = 4000;
const ESPERA_MS = 5 * 60 * 1000;

export default function TelegramConfig({ showSnack }) {
  const { loadApi, loadingApi } = useApi();
  const [estado, setEstado] = useState(null);
  const [enlace, setEnlace] = useState(null); // { enlace, qr, venceEn }
  const [esperando, setEsperando] = useState(false);
  const reloj = useRef(null);

  const cargar = () =>
    loadApi("user/telegram", true, "get")
      .then((res) => {
        setEstado(res.data);
        return res.data;
      })
      .catch(() => null);

  useEffect(() => {
    cargar();
    return () => clearInterval(reloj.current);
  }, []);

  const dejarDeEsperar = () => {
    clearInterval(reloj.current);
    setEsperando(false);
  };

  // ¿Ya tocó "Iniciar" en Telegram? Si el enlace venció sin conectarse, se
  // quita para que genere otro.
  const revisar = async (venceEn, avisarSiNo = false) => {
    const actual = await cargar();
    if (actual?.conectado && !actual.bloqueado) {
      dejarDeEsperar();
      setEnlace(null);
      showSnack("¡Listo! Tu Telegram quedó conectado.");
    } else if (Date.now() > new Date(venceEn).getTime()) {
      dejarDeEsperar();
      setEnlace(null);
      showSnack("El enlace venció. Toca «Conectar Telegram» para generar otro.", "warning");
    } else if (avisarSiNo) {
      showSnack("Todavía no aparece conectado. Toca «Iniciar» en el chat del bot.", "warning");
    }
  };

  const esperarConexion = (venceEn) => {
    clearInterval(reloj.current);
    const hasta = Date.now() + ESPERA_MS;
    setEsperando(true);
    reloj.current = setInterval(() => {
      if (Date.now() > hasta) return dejarDeEsperar();
      revisar(venceEn);
    }, CADA_MS);
  };

  // Al volver a esta pestaña (por ejemplo, desde la app de Telegram).
  useEffect(() => {
    if (!enlace) return;
    const alVolver = () => document.visibilityState === "visible" && revisar(enlace.venceEn);
    document.addEventListener("visibilitychange", alVolver);
    return () => document.removeEventListener("visibilitychange", alVolver);
  }, [enlace]);

  const conectar = () =>
    loadApi("user/telegram/codigo", true, "post", {})
      .then((res) => {
        setEnlace(res.data);
        esperarConexion(res.data.venceEn);
      })
      .catch((e) => showSnack(e.message, "error"));

  // El switch cambia al instante; si no se guardó, vuelve a como estaba.
  const pausar = (habilitado) => {
    setEstado((e) => ({ ...e, habilitado }));
    return loadApi("user/telegram", true, "put", { habilitado })
      .then((res) => showSnack(res.data.message))
      .catch((e) => {
        setEstado((prev) => ({ ...prev, habilitado: !habilitado }));
        showSnack(e.message, "error");
      });
  };

  const probar = () =>
    loadApi("user/telegram/prueba", true, "post", {})
      .then((res) => {
        showSnack(res.data.message);
        cargar();
      })
      .catch((e) => {
        showSnack(e.message, "error");
        cargar();
      });

  const desconectar = () =>
    loadApi("user/telegram", true, "delete")
      .then((res) => {
        showSnack(res.data.message);
        cargar();
      })
      .catch((e) => showSnack(e.message, "error"));

  if (!estado) return null;

  return (
    <>
      <Divider sx={{ my: 4 }} />
      <Stack gap={1.5} alignItems="flex-start">
        <Stack direction="row" alignItems="center" gap={1}>
          <FaTelegramPlane color="#229ED9" fontSize="24px" />
          <Typography variant="h5" fontWeight={600}>
            Avisos por Telegram
          </Typography>
        </Stack>
        <Typography variant="body2" color="GrayText">
          Recibe en Telegram los mismos avisos que te llegan aquí: envíos
          nuevos, aprobaciones, rechazos, reportes y mensajes del equipo.
        </Typography>

        {!estado.disponible ? (
          <Typography variant="body2" color="GrayText">
            Los avisos por Telegram todavía no están disponibles.
          </Typography>
        ) : estado.conectado ? (
          <>
            <Typography variant="body1">
              Conectado como{" "}
              <strong>
                {estado.telegram?.usuario || estado.telegram?.nombre || "tu cuenta de Telegram"}
              </strong>
              {estado.telegram?.usuario && estado.telegram?.nombre
                ? ` (${estado.telegram.nombre})`
                : ""}
            </Typography>
            {estado.bloqueado && (
              <Alert severity="warning">
                Telegram no deja escribirte: bloqueaste al bot o borraste el
                chat. Ábrelo de nuevo, toca «Iniciar» y luego «Enviar prueba».
              </Alert>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(estado.habilitado)}
                  onChange={(e) => pausar(e.target.checked)}
                  color="success"
                />
              }
              label="Recibir avisos por Telegram"
            />
            <Stack direction="row" gap={1} flexWrap="wrap">
              <LoadingButton
                variant="outlined"
                loading={loadingApi.includes("post__user/telegram/prueba")}
                onClick={probar}
              >
                Enviar prueba
              </LoadingButton>
              <LoadingButton
                color="inherit"
                loading={loadingApi.includes("delete__user/telegram")}
                onClick={desconectar}
              >
                Desconectar
              </LoadingButton>
            </Stack>
          </>
        ) : enlace ? (
          <Stack gap={1.5} alignItems="flex-start">
            <Typography variant="body2">
              1. Abre Telegram con el botón (o escanea el código con tu
              celular).
              <br />
              2. Toca <strong>«Iniciar»</strong> en el chat del bot{" "}
              {estado.bot}.{" "}
              {esperando
                ? "Esta sección se actualiza sola."
                : "Después, vuelve aquí y toca «Ya toqué Iniciar»."}
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} gap={2} alignItems="center">
              <Button
                variant="contained"
                href={enlace.enlace}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<FaTelegramPlane />}
                sx={{ bgcolor: "#229ED9", "&:hover": { bgcolor: "#1c8cc2" } }}
              >
                Abrir Telegram
              </Button>
              {enlace.qr && (
                <Box
                  component="img"
                  src={enlace.qr}
                  alt="Código QR para abrir el bot en Telegram"
                  sx={{ width: 140, height: 140, display: { xs: "none", sm: "block" } }}
                />
              )}
            </Stack>
            {!esperando && (
              <LoadingButton
                variant="outlined"
                loading={loadingApi.includes("get__user/telegram")}
                onClick={() => revisar(enlace.venceEn, true)}
              >
                Ya toqué «Iniciar»
              </LoadingButton>
            )}
            <Typography variant="caption" color="GrayText">
              El enlace es solo para tu cuenta y vence en {enlace.minutos || 30}{" "}
              minutos. Radar Freim nunca te pide contraseñas ni códigos por
              Telegram.
            </Typography>
          </Stack>
        ) : (
          <LoadingButton
            variant="contained"
            loading={loadingApi.includes("post__user/telegram/codigo")}
            onClick={conectar}
            startIcon={<FaTelegramPlane />}
            sx={{ bgcolor: "#229ED9", "&:hover": { bgcolor: "#1c8cc2" } }}
          >
            Conectar Telegram
          </LoadingButton>
        )}
      </Stack>
    </>
  );
}
